import type { ReactNode } from 'react';
import { cn } from '../lib/utils';

/**
 * A page or panel with nothing in it yet: icon in a soft accent square, a title, one line
 * on what the page is for, optional example chips, and the action that starts things.
 * Self-contained and props-driven, so it can move into a component library as is.
 */
export function EmptyState({
  icon,
  title,
  description,
  examples,
  action,
  secondaryAction,
  className,
}: {
  /** A decorative icon, e.g. a lucide icon element. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Short example phrases, shown as chips. */
  examples?: string[];
  /** The primary action (one button or link). */
  action?: ReactNode;
  /** A quiet extra action, e.g. a ghost link. */
  secondaryAction?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2.5 rounded-[14px] border border-dashed border-border bg-card px-6 py-12 text-center',
        className,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className="grid h-11 w-11 place-items-center rounded-xl bg-accent-soft text-accent [&_svg]:h-[22px] [&_svg]:w-[22px]"
        >
          {icon}
        </span>
      ) : null}
      <h2 className="m-0 mt-1 text-[17px] font-semibold">{title}</h2>
      {description ? (
        <p className="m-0 max-w-[46ch] text-sm text-muted text-pretty">{description}</p>
      ) : null}
      {examples && examples.length > 0 ? (
        <ul
          aria-label="Examples"
          className="m-0 mt-1.5 flex list-none flex-wrap justify-center gap-2 p-0"
        >
          {examples.map((example) => (
            <li key={example} className="rounded-full bg-bg px-2.5 py-1 text-xs text-muted">
              {example}
            </li>
          ))}
        </ul>
      ) : null}
      {action || secondaryAction ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
